import { useParams, Navigate, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import AdminUsers from "@/pages/AdminUsers";
import AdminAudit from "@/pages/AdminAudit";
import { RolesPanel } from "@/components/admin/RolesPanel";
import { PermissionsPanel } from "@/components/admin/PermissionsPanel";
import { PermissionRulesReport } from "@/components/admin/PermissionRulesReport";
import { ApprovalRules } from "@/components/admin/ApprovalRules";
import { ConfigMasters, Utilities } from "@/components/admin/ConfigMasters";
import { ServiceTokenPanel } from "@/components/admin/ServiceTokenPanel";
import { FinanceEmailsPanel } from "@/components/admin/FinanceEmailsPanel";
import { ReviveLmsPanel } from "@/components/admin/ReviveLmsPanel";
import { ClassifyLinesPanel } from "@/components/admin/ClassifyLinesPanel";

const Approvals = () => (<><FinanceEmailsPanel /><ApprovalRules /></>);

const Audit = () => (<><AdminAudit embedded /><Utilities /><ServiceTokenPanel /></>);

// section, title, subtitle, component, permission needed
export const ADMIN_SECTIONS = [
  ["users", "Users", "Add people, assign roles, activate or deactivate", AdminUsers, "admin.users"],
  ["roles", "Roles", "Built-in and module-specific roles with their purpose", RolesPanel, "admin.roles"],
  ["permissions", "Permissions", "What each role can do, module by module — enforced on the server", PermissionsPanel, "admin.roles"],
  ["approvals", "Approval rules", "Who approves what, self-approval and approval per LM type", Approvals, "admin.settings"],
  ["perm-report", "Permissions change report", "Which roles gain or lose access because this app version's built-in rules changed - check before publishing", PermissionRulesReport, "admin.roles"],
  ["config", "Configuration masters", "LM types, movement types, numbering, supporting lists, document categories, statuses", ConfigMasters, "admin.settings"],
  ["audit", "Audit & utilities", "Audit trail, imports and data health", Audit, "admin.audit"],
  ["revive", "Revive cancelled LMs", "Admin only - restore cancelled LMs to the status they had before cancellation", ReviveLmsPanel, "admin.settings"],
  ["classify", "Classify existing LM lines", "Admin only - one-time: mark lines of open LMs as Material or Service (preview first)", ClassifyLinesPanel, "admin.settings"],
];
const ROLES_GROUP = ["roles", "permissions", "approvals", "perm-report"];

function RolesTabs({ tabs, cur }) {
  const nav = useNavigate();
  return (
    <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200" data-testid="roles-permissions-tabs">
      {tabs.map(([k, l]) => <button key={k} onClick={() => nav(`/admin/${k}`)} data-testid={`roles-permissions-tab-${k}`}
        className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${cur === k ? "border-sky-700 font-semibold text-sky-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>)}
    </div>
  );
}

export default function Admin() {
  const { section } = useParams();
  const { can } = useMe();
  const allowed = ADMIN_SECTIONS.filter((s) => can(s[4]));
  const cur = allowed.find((s) => s[0] === section);
  if (!cur) return allowed.length ? <Navigate to={`/admin/${allowed[0][0]}`} replace /> : <Navigate to="/" replace />;
  const [, title, subtitle, C] = cur;
  const grouped = ROLES_GROUP.includes(cur[0]);
  return (
    <div data-testid={`admin-section-${cur[0]}`}>
      <PageHeader title={grouped ? "Admin · Roles & Permissions" : `Admin · ${title}`} subtitle={subtitle} />
      {grouped && <RolesTabs tabs={allowed.filter((s) => ROLES_GROUP.includes(s[0]))} cur={cur[0]} />}
      <C />
    </div>
  );
}
